/* eslint-disable max-lines-per-function */
import { authenticatedFetch } from "@lumi/data";
import { SelectValue } from "@radix-ui/react-select";
import { useState, type ReactElement } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import { Button } from "../../../shared/components/ui/button";
import { Input } from "../../../shared/components/ui/input";
import { Label } from "../../../shared/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../../shared/components/ui/select";
import { Switch } from "../../../shared/components/ui/switch";
import { useToast } from "../../../shared/hooks/use-toast";
import type { Collection, CollectionCardType, EditableCollectionTemplate, MapRevealConfig } from "../types/collections";

interface CollectionConfigEditorProps {
  readonly collection: Collection;
  readonly onSuccess: () => void;
  readonly onCancel: () => void;
}

/**
 * CollectionConfigEditor - Inline editor for collection metadata
 *
 * Displays collection settings (name, sort, cardType, visibility, template) in an inline
 * form that replaces the collection items list. Matches the pattern established by
 * MapRevealConfigEditor for consistent UX.
 */
export function CollectionConfigEditor({ collection, onSuccess, onCancel }: CollectionConfigEditorProps): ReactElement {
  const { toast } = useToast();
  const { accessToken, refreshAuth } = useAuth();
  const [name, setName] = useState(collection.name);
  const [sort, setSort] = useState(collection.sort.toString());
  const [cardType, setCardType] = useState<CollectionCardType>(collection.cardType);
  const [visible, setVisible] = useState(collection.visible);
  // Map HERO to RAIL since we don't support HERO in the editor
  const initialTemplate: EditableCollectionTemplate =
    collection.template === "HERO" ? "RAIL" : (collection.template ?? "RAIL");
  const [template, setTemplate] = useState<EditableCollectionTemplate>(initialTemplate);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  const handleSave = async () => {
    if (!accessToken) return;

    if (!name.trim()) {
      setMessage({ type: "error", text: "Name is required" });
      return;
    }

    const sortNum = Number.parseInt(sort, 10);
    if (Number.isNaN(sortNum)) {
      setMessage({ type: "error", text: "Sort must be a valid number" });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const updateData: {
        name?: string;
        sort?: number;
        cardType?: CollectionCardType;
        visible?: boolean;
        template?: EditableCollectionTemplate;
        config?: MapRevealConfig | null;
      } = {};

      if (name.trim() !== collection.name) {
        updateData.name = name.trim();
      }
      if (sortNum !== collection.sort) {
        updateData.sort = sortNum;
      }
      if (cardType !== collection.cardType) {
        updateData.cardType = cardType;
      }
      if (visible !== collection.visible) {
        updateData.visible = visible;
      }
      if (template !== (collection.template ?? "RAIL")) {
        updateData.template = template;
      }

      if (Object.keys(updateData).length === 0) {
        setMessage({ type: "error", text: "No changes to save" });
        setSaving(false);
        return;
      }

      await authenticatedFetch(
        `${API_BASE}/v1/admin/collections/${collection.key}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updateData),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      setMessage({ type: "success", text: "Collection settings saved successfully" });
      // Brief delay to show success message before transitioning back
      setTimeout(() => {
        toast({
          description: "Collection settings saved successfully",
        });
        onSuccess();
      }, 300);
    } catch (err) {
      console.error("❌ Failed to save collection:", err);
      const errorMessage = "Failed to save settings. Please try again.";
      toast({
        description: errorMessage,
        variant: "destructive",
      });
      setMessage({ type: "error", text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-white mb-2">Collection Settings</h3>
        <p className="text-xs text-neutral-400">
          Configure the display name, order, and presentation style for this collection
        </p>
      </div>

      {message && (
        <div
          className={`p-3 border rounded ${
            message.type === "success" ? "bg-green-900/20 border-green-800" : "bg-red-900/20 border-red-800"
          }`}
        >
          <p className={`text-sm ${message.type === "success" ? "text-green-400" : "text-red-400"}`}>{message.text}</p>
        </div>
      )}

      <div className="space-y-4 bg-none p-6">
        <div className="space-y-2">
          <Label htmlFor="collection-key">Key (read-only)</Label>
          <Input
            id="collection-key"
            type="text"
            value={collection.key}
            disabled
            className="cursor-not-allowed opacity-50 bg-transparent"
          />
          <p className="text-xs text-neutral-500">Collection keys cannot be changed</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="collection-name">Name</Label>
          <Input
            id="collection-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Featured, Trending Now, etc."
            className="bg-transparent"
          />
          <p className="text-xs text-neutral-500">Display name shown to users</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="collection-sort">Sort Order</Label>
          <Input
            id="collection-sort"
            type="number"
            className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-transparent"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            placeholder="0"
          />
          <p className="text-xs text-neutral-500">
            Lower values appear first (0-9: system, 10-19: distributors, 20+: custom)
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="collection-template">Template</Label>
          <Select value={template} onValueChange={(value) => setTemplate(value as "RAIL" | "MAP_REVEAL" | "PROVIDER")}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select template" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="RAIL">Content Rail</SelectItem>
              <SelectItem value="MAP_REVEAL">Map Reveal Section</SelectItem>
              <SelectItem value="PROVIDER">Provider Showcase</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-neutral-500">Choose the layout template for this collection</p>
        </div>

        {template === "RAIL" && (
          <div className="space-y-2">
            <Label htmlFor="collection-card-type">Card Type</Label>
            <Select
              value={cardType}
              onValueChange={(value) => setCardType(value as "VERTICAL" | "HORIZONTAL" | "SQUARE")}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select card type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="VERTICAL">Vertical (2:3 Portrait)</SelectItem>
                <SelectItem value="HORIZONTAL">Horizontal (16:9 Landscape)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-neutral-500">Choose which card style to use for this collection</p>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="collection-visible">Visibility</Label>
          <div className="flex items-center gap-3">
            <Switch id="collection-visible" checked={visible} onCheckedChange={setVisible} />
            <span className="text-sm text-neutral-400">
              {visible ? "Visible on home page" : "Hidden from home page"}
            </span>
          </div>
          <p className="text-xs text-neutral-500">Hidden collections won't appear on the home page</p>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button onClick={onCancel} variant="outline" size="sm" disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={saving}
            variant="outline"
            size="sm"
            className="hover:bg-green-500/10 hover:text-green-500 hover:border-green-500/50"
          >
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </div>
    </div>
  );
}
